import { Alert, Card, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import { fetchCrowsNestNorthStar } from 'src/utils/crowsNestApi'
import {
  buildNorthStarWidgetModel,
  getNorthStarWeekOverWeekRanges,
} from 'src/utils/crowsNestNorthStar'
import {
  CROWS_NEST_NORTH_STAR_METRIC_TIPS,
  CROWS_NEST_TOOLTIPS,
} from 'src/utils/crowsNestTooltips'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

/**
 * Crow’s Nest north star — Free venues with paywall opportunities, week-over-week.
 */
const CrowsNestNorthStarWidget = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [model, setModel] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const ranges = getNorthStarWeekOverWeekRanges()
        const [current, prior] = await Promise.all([
          fetchCrowsNestNorthStar({
            fromDate: ranges.current.fromDate,
            toDate: ranges.current.toDate,
            granularity: 'day',
          }),
          fetchCrowsNestNorthStar({
            fromDate: ranges.prior.fromDate,
            toDate: ranges.prior.toDate,
            granularity: 'day',
          }),
        ])

        if (cancelled) return

        setModel(
          buildNorthStarWidgetModel({
            currentTotals: current.totals,
            priorTotals: prior.totals,
            ranges,
            definition: current.definition,
          }),
        )
      } catch (err) {
        if (!cancelled) {
          setError(
            crowsNestErrorMessage(
              err,
              "Couldn't load the north star. Try again in a moment.",
            ),
          )
          setModel(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <WidgetCard
      data-testid='crows-nest-north-star'
      title={
        <TitleRow>
          <span>North star</span>
          <Tooltip title={CROWS_NEST_TOOLTIPS.northStarSection}>
            <InfoCircleOutlined />
          </Tooltip>
        </TitleRow>
      }
    >
      {error && !model ? <Alert type='error' showIcon message={error} /> : null}

      {loading && !model ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {model ? (
        <Body>
          <PrimaryBlock>
            <PrimaryLabel>
              <CrowsNestTipLabel
                label={model.primary.label}
                tip={
                  CROWS_NEST_NORTH_STAR_METRIC_TIPS[model.primary.key] ||
                  CROWS_NEST_TOOLTIPS.freeVenuesAtPaywall
                }
              />
            </PrimaryLabel>
            <PrimaryValue data-testid='north-star-primary-value'>
              {model.primary.value}
            </PrimaryValue>
            <Tooltip title={CROWS_NEST_TOOLTIPS.weekOverWeekDelta}>
              <Delta
                data-testid='north-star-wow-delta'
                $direction={model.primary.delta.direction}
              >
                {model.primary.deltaLabel}
              </Delta>
            </Tooltip>
            <RangeNote>
              Last 7 days {model.ranges.current.label} · Prior 7 days{' '}
              {model.ranges.prior.label}
            </RangeNote>
          </PrimaryBlock>

          <SecondaryRow>
            {model.secondary.map(item => (
              <SecondaryStat key={item.key}>
                <SecondaryLabel>
                  <CrowsNestTipLabel
                    label={item.label}
                    tip={CROWS_NEST_NORTH_STAR_METRIC_TIPS[item.key]}
                  />
                </SecondaryLabel>
                <SecondaryValue>{item.value}</SecondaryValue>
                {item.deltaLabel ? (
                  <Tooltip title={CROWS_NEST_TOOLTIPS.weekOverWeekDelta}>
                    <SecondaryDelta $direction={item.delta?.direction}>
                      {item.deltaLabel}
                    </SecondaryDelta>
                  </Tooltip>
                ) : null}
              </SecondaryStat>
            ))}
          </SecondaryRow>
        </Body>
      ) : null}
    </WidgetCard>
  )
}

const WidgetCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg || '24px'};
`

const PrimaryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PrimaryLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 14px;
  font-weight: 500;
`

const PrimaryValue = styled.div`
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  font-size: 48px;
  font-weight: 700;
  line-height: 1.1;
`

const Delta = styled.div`
  color: ${({ $direction, theme }) => {
    if ($direction === 'up') return theme.colours.seaweedGreen || '#2f6b3a'
    if ($direction === 'down') return theme.colours.tomatoRed || '#b33a3a'
    return theme.colours.oliveGrey || '#666'
  }};
  cursor: help;
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
`

const RangeNote = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-size: 12px;
  margin-top: 4px;
`

const SecondaryRow = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`

const SecondaryStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const SecondaryLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 13px;
`

const SecondaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
`

const SecondaryDelta = styled.div`
  color: ${({ $direction, theme }) => {
    if ($direction === 'up') return theme.colours.seaweedGreen || '#2f6b3a'
    if ($direction === 'down') return theme.colours.tomatoRed || '#b33a3a'
    return theme.colours.oliveGrey || '#666'
  }};
  cursor: help;
  display: inline-block;
  font-size: 12px;
  width: fit-content;
`

export default CrowsNestNorthStarWidget
